import { PROCESS_STEPS } from '../../mocks/siteData';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface ProcessSectionProps {
  className?: string;
}

export default function ProcessSection({ className = '' }: ProcessSectionProps) {
  const { ref, visible } = useScrollAnimation();

  return (
    <section className={`bg-stone-950 py-32 px-6 lg:px-16 xl:px-24 ${className}`}>
      <div className="max-w-6xl mx-auto">

        {/* Top heading */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-20">
          <div>
            <p className="text-accent text-xs font-semibold tracking-[0.2em] uppercase mb-4">Our Process</p>
            <h2 className="font-serif text-4xl lg:text-6xl text-white leading-tight max-w-xl"
              style={{ letterSpacing: '-0.02em' }}
            >
              A Clear, Professional Remodeling Process
            </h2>
          </div>
          <p className="text-stone-500 text-sm leading-relaxed max-w-sm">
            From first conversation to final walkthrough — every step is structured, transparent, and built around delivering exactly what you envisioned.
          </p>
        </div>

        {/* Steps grid */}
        <div
          ref={ref}
          className={`grid grid-cols-1 md:grid-cols-2 gap-px bg-stone-800/40 rounded-2xl overflow-hidden transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {PROCESS_STEPS.map((step, i) => (
            <div
              key={step.step}
              className="bg-stone-900/80 p-10 flex flex-col justify-between group hover:bg-stone-900 transition-colors duration-300"
            >
              <div className="flex items-start justify-between mb-8">
                <span
                  className="font-serif text-6xl lg:text-7xl text-accent/20 font-bold leading-none group-hover:text-accent/30 transition-colors duration-300"
                  style={{ letterSpacing: '-0.03em' }}
                >
                  {step.step}
                </span>
                <div className="w-8 h-8 rounded-full border border-stone-700 group-hover:border-accent flex items-center justify-center flex-shrink-0 transition-colors duration-300">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-stone-600 group-hover:text-accent text-xs transition-colors" />
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-serif text-xl lg:text-2xl text-white mb-3 leading-snug">{step.title}</h3>
                <p className="text-stone-500 text-sm leading-relaxed">{step.description}</p>
              </div>
              {/* Bottom accent line */}
              <div className="mt-8 h-px bg-gradient-to-r from-accent/0 via-accent/30 to-accent/0 group-hover:from-accent/0 group-hover:via-accent group-hover:to-accent/0 transition-all duration-500" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
